import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Spinner,
  Alert,
  Container,
  Breadcrumb,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FiDownload, FiRefreshCw, FiBarChart2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfWeek, startOfYear } from "date-fns";
import DatePicker from "react-datepicker";
import Reportselect from "./Reportselect";
import Pagination from "../Constant/Pagination";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { getEmployeeAssetReport } from "../../services/ReportServices";
import { searchcategoryOptionApi } from "../../services/SearchService";

const EmployeeAssetReport = () => {
  const periodOptions = [
    { label: "All", value: "all" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "7_days" },
    { label: "Last 15 Days", value: "15_days" },
    { label: "Last 30 Days", value: "30_days" },
    { label: "Last Month", value: "last_month" },
    { label: "Last 3 Months", value: "3_months" },
    { label: "Custom", value: "custom" },
  ];

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Category and Subcategory state
  const [dropdownCategory, setDropdownCategory] = useState(null);
  const [dropdownSubCategory, setDropdownSubCategory] = useState(null);
  const [dropdownCategoryOption, setDropdownCategoryOption] = useState([]);
  const [dropdownSubCategoryOption, setDropdownSubCategoryOption] = useState([]);
  const [filteredSubcategoryOptions, setFilteredSubCategoryOptions] = useState([]);

  const [selectedPeriod, setSelectedPeriod] = useState(periodOptions[4]);

  const [param, setParam] = useState({
    page: 1,
    page_size: 10,
    query: "",
    employee: "",
    department: "",
    category_id: "",
    sub_category_id: "",
    period: "30_days", // Default to "Last 30 Days"
    start_date: "",
    end_date: "",
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "dd MMM yyyy");
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return `₹${value.toLocaleString("en-IN")}`;
  };

  // Load categories and subcategories
  useEffect(() => {
    searchcategoryOptionApi()
      .then((res) => {
        const categoryOpts =
          res?.data?.results?.map((item) => ({
            label: item.name,
            value: item.id,
            subcategories: item.subcategories || [],
          })) || [];

        setDropdownCategoryOption(categoryOpts);

        const allSubCats = categoryOpts.flatMap((cat) =>
          cat.subcategories.map((sub) => ({
            label: sub.name,
            value: sub.id,
            categoryId: cat.value,
          }))
        );

        setDropdownSubCategoryOption(allSubCats);
        setFilteredSubCategoryOptions(allSubCats);
      })
      .catch(() => toast.error("Failed to load categories"));
  }, []);

  // Handle period changes
  useEffect(() => {
    const today = new Date();
    let start_date = "";
    let end_date = "";

    if (!selectedPeriod || selectedPeriod.value === "all") {
      setParam((prev) => ({
        ...prev,
        period: "all",
        start_date: "",
        end_date: "",
        page: 1,
      }));
      return;
    }

    switch (selectedPeriod.value) {
      case "yesterday":
        start_date = format(subDays(today, 1), "yyyy-MM-dd");
        end_date = format(subDays(today, 1), "yyyy-MM-dd");
        break;
      case "7_days":
        start_date = format(subDays(today, 6), "yyyy-MM-dd");
        end_date = format(today, "yyyy-MM-dd");
        break;
      case "15_days":
        start_date = format(subDays(today, 14), "yyyy-MM-dd");
        end_date = format(today, "yyyy-MM-dd");
        break;
      case "30_days":
        start_date = format(subDays(today, 29), "yyyy-MM-dd");
        end_date = format(today, "yyyy-MM-dd");
        break;
      case "last_month":
        const firstDayLastMonth = startOfMonth(subMonths(today, 1));
        const lastDayLastMonth = endOfMonth(subMonths(today, 1));
        start_date = format(firstDayLastMonth, "yyyy-MM-dd");
        end_date = format(lastDayLastMonth, "yyyy-MM-dd");
        break;
      case "3_months":
        start_date = format(subMonths(today, 3), "yyyy-MM-dd");
        end_date = format(today, "yyyy-MM-dd");
        break;
      case "custom":
        // Don't set dates - they'll be picked by date picker
        break;
      default:
        start_date = "";
        end_date = "";
    }

    setParam((prev) => ({
      ...prev,
      period: selectedPeriod.value,
      start_date,
      end_date,
      page: 1,
    }));
  }, [selectedPeriod]);

  // Fetch data when params change
  useEffect(() => {
    const delay = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(delay);
  }, [param]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      if (param.period === "custom") {
        if (!param.start_date || !param.end_date) {
          throw new Error(
            "Please select both start and end dates for custom period"
          );
        }

        const start = new Date(param.start_date);
        const end = new Date(param.end_date);

        if (start > end) {
          throw new Error("Start date cannot be after end date");
        }

        const maxDateRange = 365 * 5; // 5 years
        const diffInDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));

        if (diffInDays > maxDateRange) {
          throw new Error("Date range cannot exceed 5 years");
        }
      }

      const response = await getEmployeeAssetReport({
        ...param,
        export: false,
      });

      if (!response.data) {
        throw new Error("Invalid response format from server");
      }

      setReportData(response.data.results || []);
      setTotalCount(response.data.count || response.data.results?.length || 0);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch employee asset report";

      toast.error(errorMessage);
      setError(errorMessage);
      setReportData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (selected) => {
    setDropdownCategory(selected);
    setDropdownSubCategory(null);
    const filtered = selected
      ? dropdownSubCategoryOption.filter(
          (sub) => sub.categoryId === selected.value
        )
      : dropdownSubCategoryOption;

    setFilteredSubCategoryOptions(filtered);
    setParam((prev) => ({
      ...prev,
      category_id: selected?.value || "",
      sub_category_id: "",
      page: 1,
    }));
  };

  const handleSubCategoryChange = (selected) => {
    setDropdownSubCategory(selected);
    const relatedCat = dropdownCategoryOption.find(
      (cat) => cat.value === selected?.categoryId
    );
    if (
      relatedCat &&
      (!dropdownCategory || relatedCat.value !== dropdownCategory.value)
    ) {
      setDropdownCategory(relatedCat);
      setFilteredSubCategoryOptions(
        dropdownSubCategoryOption.filter(
          (sub) => sub.categoryId === relatedCat.value
        )
      );
    }

    setParam((prev) => ({
      ...prev,
      category_id: relatedCat?.value || "",
      sub_category_id: selected?.value || "",
      page: 1,
    }));
  };

  const handleSearchChange = (value) => {
    setParam((prev) => ({
      ...prev,
      query: value,
      page: 1,
    }));
  };

  const handleEmployeeSearch = (value) => {
    setParam((prev) => ({
      ...prev,
      employee: value,
      page: 1,
    }));
  };

  const handleDepartmentSearch = (value) => {
    setParam((prev) => ({
      ...prev,
      department: value,
      page: 1,
    }));
  };

  const handlePageChange = (selectedPage) => {
    setParam((prev) => ({
      ...prev,
      page: selectedPage.selected + 1,
    }));
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const response = await getEmployeeAssetReport({ ...param, export: true });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `employee_asset_report_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export started successfully");
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <nav>
          <h2 className="text-primary">Employee Asset Report</h2>
          <ol className="breadcrumb breadcrumb-arrow mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Employee Asset Report
            </li>
          </ol>
        </nav>

        <div
          className="d-flex align-items-center gap-2 ms-auto mt-2 mt-md-0"
          style={{ zIndex: 10 }}
        >
          <div style={{ width: "200px" }}>
            <Reportselect />
          </div>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exportLoading || loading}
            style={{ height: "41px" }}
          >
            {exportLoading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FiDownload className="me-1" />
            )}
            Export
          </Button>
        </div>
      </div>

      <Card style={{ minHeight: "350px" }}>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">
            <FiBarChart2 className="me-2" /> Employee Asset Report
          </h5>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <DebounceSmallSearchInput
              value={param.query}
              handleSearchChange={handleSearchChange}
              placeholder="Search assets..."
            />

            <GlobalSearchableDropdown
              options={dropdownCategoryOption}
              selected={dropdownCategory}
              onSelect={handleCategoryChange}
              placeholder="Category"
              width="180px"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />

            <GlobalSearchableDropdown
              options={filteredSubcategoryOptions}
              selected={dropdownSubCategory}
              onSelect={handleSubCategoryChange}
              placeholder="Sub-Category"
              width="180px"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />

            <GlobalSearchableDropdown
              options={periodOptions}
              selected={selectedPeriod}
              onSelect={setSelectedPeriod}
              placeholder="Period"
              width="150px"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />

            {selectedPeriod?.value === "custom" && (
              <div style={{ width: "260px" }}>
                <DatePicker
                  selectsRange
                  startDate={
                    param.start_date ? new Date(param.start_date) : null
                  }
                  endDate={param.end_date ? new Date(param.end_date) : null}
                  onChange={([start, end]) => {
                    setParam((prev) => ({
                      ...prev,
                      period: "custom",
                      start_date: start ? format(start, "yyyy-MM-dd") : "",
                      end_date: end ? format(end, "yyyy-MM-dd") : "",
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                />
              </div>
            )}

            
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger">
              {error}
              <Button variant="link" size="sm" onClick={fetchData}>
                <FiRefreshCw /> Try again
              </Button>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading report data...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table
                  size="sm"
                  className="align-middle text-center text-nowrap small rounded"
                >
                  <thead className="table-light">
                    <tr>
                      <th>Employee</th>
                      <th>Department</th>
                      <th>Designation</th>
                      <th>Asset Number</th>
                      <th>Asset Name</th>
                      <th>Category</th>
                      <th>Subcategory</th>
                      <th>Allocated Date</th>
                      <th>Current Value</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          <small>No assets found matching your criteria</small>
                        </td>
                      </tr>
                    ) : (
                      reportData.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.employee_name || "-"}</td>
                          <td>{item.department || "-"}</td>
                          <td>{item.designation || "-"}</td>
                          <td>{item.asset_number || "-"}</td>
                          <td>{item.asset_name || "-"}</td>
                          <td>{item.category || "-"}</td>
                          <td>{item.subcategory || "-"}</td>
                          <td>{formatDate(item.allocated_date)}</td>
                          <td>{formatCurrency(item.cost)}</td>
                          <td>{item.status || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <Pagination
                currentPage={param.page}
                recordsPage={param.page_size}
                dataLength={totalCount}
                handlePagination={handlePageChange}
                showPageInfo
                disabled={loading}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EmployeeAssetReport;
